import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Clock, Crown, Sparkles, CheckCircle2, Headphones, BookOpen, Zap } from 'lucide-react';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
type Step = 'upsell1' | 'upgrade_detail' | 'upgrade_downsell' | 'upsell2';
const UPSELL_IMG = 'https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Libro_dorado_comprimida.png?v=1775619938';
const DOWNSELL_IMG = 'https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Libro_negro_con_el_numero_888_comprimida.png?v=1775620039';
export function UpsellPopup({
  isOpen,
  onClose
}: UpsellPopupProps) {
  const [step, setStep] = useState<Step>('upsell1');
  const [timeLeft, setTimeLeft] = useState(300);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  useEffect(() => {
    if (isOpen && !imagesLoaded) {
      const img1 = new Image();
      const img2 = new Image();
      let loaded = 0;
      const onLoad = () => {
        loaded++;
        if (loaded >= 2) setImagesLoaded(true);
      };
      img1.onload = onLoad;
      img2.onload = onLoad;
      img1.src = UPSELL_IMG;
      img2.src = DOWNSELL_IMG;
    }
  }, [isOpen, imagesLoaded]);
  useEffect(() => {
    if (isOpen) {
      setStep('upsell1');
      setTimeLeft(300);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const trackAndRedirect = (url: string, contentId: string, value: number) => {
    try {
      if (typeof window !== 'undefined' && (window as any).fbq) {
        ;
        (window as any).fbq('track', 'AddToCart', {
          content_ids: [contentId],
          content_type: 'product',
          value,
          currency: 'USD'
        });
      }
    } catch (e) {
      console.warn('Facebook Pixel tracking failed:', e);
    }
    window.location.href = url;
  };
  // Single bonus upsell
  const handleAcceptBonus = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51937703297326:1', '51937703297326', 12.78);
  };
  // Full package purchase
  const handleBuyUpgrade = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51937439318318:1', '51937439318318', 23.0);
  };
  // Full package 20% off
  const handleBuyUpgradeDiscount = () => {
    trackAndRedirect('https://betterdailyguides.com/cart/51956021231918:1', '51956021231918', 18.4);
  };
  // Single bonus downsell 30% off
  const handleAcceptBonusDiscount = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51937708212526:1', '51937708212526', 10.86);
  };
  // Original product only
  const handleDeclineAll = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51934295621934:1', '51934295621934', 6.39);
  };
  if (!isOpen) return null;
  const popupMotion = {
    initial: {
      opacity: 0,
      scale: 0.85,
      y: 30
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: -20
    },
    transition: {
      type: 'spring' as const,
      damping: 22,
      stiffness: 280
    }
  };
  return <AnimatePresence mode="wait" data-id="element-186">
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-3 sm:p-6 md:p-8 overflow-y-auto" data-id="element-187">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/92 backdrop-blur-md" data-id="element-188" />

        {/* ===== STEP 1: UPSELL — Bonus + Upgrade option ===== */}
        {step === 'upsell1' && <motion.div key="upsell1" {...popupMotion} className="relative w-full max-w-[400px] sm:max-w-[440px] md:max-w-lg z-10 rounded-3xl overflow-hidden my-auto" style={{
        boxShadow: '0 0 80px rgba(212,175,55,0.3), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-189">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-gold via-gold/30 to-gold/50 p-[2px]" data-id="element-190">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-191" />
            </div>
            <div className="relative z-10" data-id="element-192">
              <div className="bg-red-600 text-white text-center py-2 flex items-center justify-center gap-2 text-[11px] sm:text-xs font-bold tracking-wider" data-id="element-193">
                <Clock className="w-3.5 h-3.5 animate-pulse" data-id="element-194" />
                OFERTA EXPIRA EN {formatTime(timeLeft)}
              </div>

              <div className="relative min-h-[130px] sm:min-h-[160px] flex items-end justify-center overflow-hidden" data-id="element-195">
                <img src={UPSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-25 scale-110 blur-[1px]" data-id="element-196" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-transparent" data-id="element-197" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/40 to-transparent" data-id="element-198" />
                <div className="absolute inset-0 opacity-30" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.35) 0%, transparent 65%)'
            }} data-id="element-199" />
                <div className="relative z-10 text-center px-5 pb-3 pt-6" data-id="element-200">
                  <div className="inline-block bg-gold/20 backdrop-blur-sm text-gold text-[9px] sm:text-[10px] font-black uppercase tracking-[3px] px-4 py-1.5 rounded-full mb-2 border border-gold/30" data-id="element-201">
                    ★ BONO EXCLUSIVO ★
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight drop-shadow-lg" data-id="element-202">
                    El Código de
                    <br data-id="element-203" />
                    <span className="text-gold drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-id="element-204">
                      Riqueza Suprema
                    </span>
                  </h3>
                </div>
              </div>

              <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-3 space-y-3" data-id="element-205">
                {/* UPGRADE OPTION */}
                <div className="relative rounded-2xl overflow-hidden" data-id="element-206">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-gold via-yellow-300 to-gold p-[2px] animate-pulse" data-id="element-207">
                    <div className="w-full h-full rounded-2xl bg-[#0c0c0c]" data-id="element-208" />
                  </div>
                  <div className="relative z-10 p-4" data-id="element-209">
                    <div className="flex items-center justify-center gap-1.5 mb-2" data-id="element-210">
                      <Crown className="w-4 h-4 text-gold" data-id="element-211" />
                      <span className="text-gold text-[10px] sm:text-xs font-black uppercase tracking-[2px]" data-id="element-212">
                        RECOMENDADO
                      </span>
                      <Crown className="w-4 h-4 text-gold" data-id="element-213" />
                    </div>
                    <h4 className="text-white font-black text-sm sm:text-base text-center mb-1" data-id="element-214">
                      PAQUETE COMPLETO 888
                    </h4>
                    <p className="text-white/50 text-[10px] sm:text-xs text-center mb-3" data-id="element-215">
                      4 Bonos + Audios de Meditación + Todo Incluido
                    </p>
                    <motion.button onClick={() => setStep('upgrade_detail')} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.97
                }} className="w-full bg-gradient-to-r from-gold via-yellow-400 to-gold text-dark font-black text-sm sm:text-base py-3.5 sm:py-4 rounded-xl shadow-[0_8px_30px_rgba(212,175,55,0.5)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-216">
                      MUÉSTRAME MÁS
                      <ChevronRight className="w-5 h-5" data-id="element-217" />
                    </motion.button>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-2" data-id="element-218">
                  <div className="flex-1 h-px bg-white/10" data-id="element-219" />
                  <span className="text-white/30 text-[10px] font-bold uppercase tracking-wider" data-id="element-220">
                    o elige
                  </span>
                  <div className="flex-1 h-px bg-white/10" data-id="element-221" />
                </div>

                {/* SINGLE BONUS */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-id="element-222">
                  <h4 className="text-white/80 font-bold text-sm text-center mb-1" data-id="element-223">
                    Solo el Bono Riqueza Suprema
                  </h4>
                  <div className="text-center mb-3" data-id="element-224">
                    <span className="text-3xl sm:text-4xl font-black text-gold" data-id="element-225">
                      +$6.39
                    </span>
                    <span className="text-white/30 text-[11px] ml-2" data-id="element-226">
                      Total: $12.78
                    </span>
                  </div>
                  <motion.button onClick={handleAcceptBonus} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.97
              }} className="w-full bg-white/10 hover:bg-white/15 text-white font-bold text-sm py-3 rounded-xl transition-colors flex items-center justify-center gap-2 uppercase tracking-wider border border-white/10" data-id="element-227">
                    AÑADIR A MI ORDEN
                    <ChevronRight className="w-4 h-4" data-id="element-228" />
                  </motion.button>
                </div>

                <button onClick={() => setStep('upsell2')} className="w-full text-center text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide pt-1" data-id="element-229">
                  No gracias, continuar sin el bono
                </button>
              </div>
            </div>
          </motion.div>}

        {/* ===== STEP 2: UPGRADE DETAIL — Full package breakdown ===== */}
        {step === 'upgrade_detail' && <motion.div key="upgrade_detail" {...popupMotion} className="relative w-full max-w-[400px] sm:max-w-[440px] md:max-w-lg z-10 rounded-3xl overflow-hidden my-auto" style={{
        boxShadow: '0 0 100px rgba(212,175,55,0.35), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-230">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-gold via-gold/40 to-gold/60 p-[2px]" data-id="element-231">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-232" />
            </div>
            <div className="relative z-10" data-id="element-233">
              {/* Header */}
              <div className="bg-gradient-to-r from-gold/30 via-gold/15 to-gold/30 border-b border-gold/20 py-3 text-center" data-id="element-234">
                <div className="flex items-center justify-center gap-2" data-id="element-235">
                  <Crown className="w-5 h-5 text-gold" data-id="element-236" />
                  <span className="text-gold font-black text-sm sm:text-base uppercase tracking-[2px]" data-id="element-237">
                    PAQUETE COMPLETO 888
                  </span>
                  <Crown className="w-5 h-5 text-gold" data-id="element-238" />
                </div>
              </div>

              {/* BG image */}
              <div className="relative min-h-[100px] sm:min-h-[120px] flex items-end justify-center overflow-hidden" data-id="element-239">
                <img src={UPSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-20 scale-110 blur-[1px]" data-id="element-240" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/80 to-[#070707]/30" data-id="element-241" />
                <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(212,175,55,0.3) 0%, transparent 65%)'
            }} data-id="element-242" />
                <div className="relative z-10 text-center px-5 pb-3 pt-5" data-id="element-243">
                  <h3 className="font-serif text-xl sm:text-2xl font-black text-white leading-tight drop-shadow-lg" data-id="element-244">
                    Tu Transformación{' '}
                    <span className="text-gold" data-id="element-245">Completa</span>
                  </h3>
                </div>
              </div>

              {/* Package contents */}
              <div className="px-4 sm:px-5 pt-3 pb-1" data-id="element-246">
                <p className="text-white/40 text-[10px] uppercase tracking-[2px] font-bold text-center mb-3" data-id="element-247">
                  Todo lo que incluye
                </p>
                <div className="space-y-2.5" data-id="element-248">
                  <div className="flex items-start gap-3 bg-gold/8 border border-gold/15 rounded-xl px-3 py-2.5" data-id="element-249">
                    <BookOpen className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-250" />
                    <div data-id="element-251">
                      <span className="text-white font-bold text-xs sm:text-sm" data-id="element-252">
                        Bono 1: El Código de Riqueza Suprema
                      </span>
                      <p className="text-white/40 text-[10px]" data-id="element-253">
                        Reprogramación financiera avanzada
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gold/8 border border-gold/15 rounded-xl px-3 py-2.5" data-id="element-254">
                    <Zap className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-255" />
                    <div data-id="element-256">
                      <span className="text-white font-bold text-xs sm:text-sm" data-id="element-257">
                        Bono 2: Protocolo de Activación Cuántica
                      </span>
                      <p className="text-white/40 text-[10px]" data-id="element-258">
                        Elimina bloqueos subconscientes en 7 días
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gold/8 border border-gold/15 rounded-xl px-3 py-2.5" data-id="element-259">
                    <Sparkles className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-260" />
                    <div data-id="element-261">
                      <span className="text-white font-bold text-xs sm:text-sm" data-id="element-262">
                        Bono 3: Guía de Manifestación Acelerada
                      </span>
                      <p className="text-white/40 text-[10px]" data-id="element-263">
                        Multiplica el efecto del Código 888 x10
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gold/8 border border-gold/15 rounded-xl px-3 py-2.5" data-id="element-264">
                    <Headphones className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-265" />
                    <div data-id="element-266">
                      <span className="text-white font-bold text-xs sm:text-sm" data-id="element-267">
                        Bono 4: Audios de Meditación Premium
                      </span>
                      <p className="text-white/40 text-[10px]" data-id="element-268">
                        Frecuencias 432Hz + 888Hz para meditación profunda
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 mt-4 mb-1" data-id="element-269">
                  <CheckCircle2 className="w-4 h-4 text-gold" data-id="element-270" />
                  <span className="text-gold text-[10px] sm:text-xs font-bold uppercase tracking-wider" data-id="element-271">
                    Acceso inmediato y de por vida
                  </span>
                </div>
              </div>

              {/* Price + CTA */}
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-2" data-id="element-272">
                <div className="text-center mb-3" data-id="element-273">
                  <span className="text-4xl sm:text-5xl font-black text-gold tracking-tight drop-shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-274">
                    $23
                  </span>
                  <div className="text-white/35 text-[11px] mt-1" data-id="element-275">
                    Pago único · Todo incluido
                  </div>
                </div>
                <motion.button onClick={handleBuyUpgrade} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-gold via-yellow-400 to-gold text-dark font-black text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-[0_10px_40px_rgba(212,175,55,0.5)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-276">
                  EMPEZAR MI TRANSFORMACIÓN
                  <ChevronRight className="w-5 h-5" data-id="element-277" />
                </motion.button>

                <button onClick={() => setStep('upgrade_downsell')} className="w-full text-center mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-278">
                  No gracias, es muy caro para mí
                </button>
              </div>
            </div>
          </motion.div>}

        {/* ===== STEP 3: UPGRADE DOWNSELL — 20% off full package ===== */}
        {step === 'upgrade_downsell' && <motion.div key="upgrade_downsell" {...popupMotion} className="relative w-full max-w-[400px] sm:max-w-[440px] md:max-w-lg z-10 rounded-3xl overflow-hidden my-auto" style={{
        boxShadow: '0 0 80px rgba(239,68,68,0.2), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-279">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-red-500 via-red-500/25 to-orange-500/30 p-[2px]" data-id="element-280">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-281" />
            </div>
            <div className="relative z-10" data-id="element-282">
              <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white text-center py-2.5 px-4" data-id="element-283">
                <div className="font-black text-xs sm:text-sm uppercase tracking-wider animate-pulse" data-id="element-284">
                  ⚡ ÚLTIMA OPORTUNIDAD — 20% OFF ⚡
                </div>
              </div>

              <div className="relative min-h-[120px] sm:min-h-[140px] flex items-end justify-center overflow-hidden" data-id="element-285">
                <img src={DOWNSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-20 scale-110 blur-[1px]" data-id="element-286" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-transparent" data-id="element-287" />
                <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(239,68,68,0.3) 0%, transparent 65%)'
            }} data-id="element-288" />
                <div className="relative z-10 text-center px-5 pb-3 pt-5" data-id="element-289">
                  <h3 className="font-serif text-xl sm:text-2xl font-black text-white leading-tight drop-shadow-lg" data-id="element-290">
                    ¿El precio es un problema?
                    <br data-id="element-291" />
                    <span className="text-green-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.3)]" data-id="element-292">
                      Toma un 20% OFF
                    </span>
                  </h3>
                </div>
              </div>

              <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-3 text-center" data-id="element-293">
                <p className="text-white/50 text-xs sm:text-sm mb-3" data-id="element-294">
                  El Paquete Completo 888 — 4 Bonos + Audios de Meditación
                </p>

                <div className="mb-4" data-id="element-295">
                  <div className="inline-block bg-green-400/15 text-green-400 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider border border-green-400/20 mb-2" data-id="element-296">
                    20% DESCUENTO EXCLUSIVO
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2" data-id="element-297">
                    <span className="text-xl text-white/20 line-through font-bold" data-id="element-298">
                      $23
                    </span>
                    <span className="text-4xl sm:text-5xl font-black text-green-400 tracking-tight drop-shadow-[0_0_20px_rgba(74,222,128,0.3)]" data-id="element-299">
                      $18.40
                    </span>
                  </div>
                  <div className="text-white/35 text-[11px] mt-1" data-id="element-300">
                    Ahorras $4.60
                  </div>
                </div>

                <motion.button onClick={handleBuyUpgradeDiscount} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-green-500 to-emerald-400 text-dark font-black text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-[0_8px_35px_rgba(74,222,128,0.35)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-301">
                  APLICAR 20% DESCUENTO
                  <ChevronRight className="w-5 h-5" data-id="element-302" />
                </motion.button>

                <button onClick={() => setStep('upsell1')} className="mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-303">
                  No gracias, volver a la oferta anterior
                </button>
              </div>
            </div>
          </motion.div>}

        {/* ===== STEP 4: SINGLE BONUS DOWNSELL — 30% off ===== */}
        {step === 'upsell2' && <motion.div key="upsell2" {...popupMotion} className="relative w-full max-w-[400px] sm:max-w-[440px] md:max-w-lg z-10 rounded-3xl overflow-hidden my-auto" style={{
        boxShadow: '0 0 80px rgba(239,68,68,0.2), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-304">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-red-500 via-red-500/25 to-orange-500/30 p-[2px]" data-id="element-305">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-306" />
            </div>
            <div className="relative z-10" data-id="element-307">
              <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white text-center py-2.5 px-4" data-id="element-308">
                <div className="font-black text-xs sm:text-sm uppercase tracking-wider animate-pulse" data-id="element-309">
                  ⚡ ÚLTIMA OPORTUNIDAD ⚡
                </div>
              </div>

              <div className="relative min-h-[130px] sm:min-h-[160px] flex items-end justify-center overflow-hidden" data-id="element-310">
                <img src={DOWNSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-20 scale-110 blur-[1px]" data-id="element-311" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-transparent" data-id="element-312" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/40 to-transparent" data-id="element-313" />
                <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(239,68,68,0.3) 0%, transparent 65%)'
            }} data-id="element-314" />
                <div className="relative z-10 text-center px-5 pb-3 pt-6" data-id="element-315">
                  <h3 className="font-serif text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight drop-shadow-lg" data-id="element-316">
                    ¡Espera! Toma un
                    <br data-id="element-317" />
                    <span className="text-green-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.3)]" data-id="element-318">
                      30% OFF
                    </span>
                  </h3>
                  <p className="text-white/50 text-xs sm:text-sm mt-2" data-id="element-319">
                    El Código de Riqueza Suprema
                  </p>
                </div>
              </div>

              <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-3 text-center" data-id="element-320">
                <div className="mb-4" data-id="element-321">
                  <div className="flex items-center justify-center gap-2 mb-2" data-id="element-322">
                    <span className="text-lg text-white/20 line-through font-bold" data-id="element-323">
                      $6.39
                    </span>
                    <span className="text-4xl sm:text-5xl font-black text-green-400 tracking-tight drop-shadow-[0_0_20px_rgba(74,222,128,0.3)]" data-id="element-324">
                      $4.47
                    </span>
                  </div>
                  <div className="inline-block bg-green-400/15 text-green-400 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider border border-green-400/20" data-id="element-325">
                    AHORRAS $1.92
                  </div>
                  <div className="text-white/35 text-[11px] mt-2" data-id="element-326">
                    Total con descuento: $10.86
                  </div>
                </div>

                <motion.button onClick={handleAcceptBonusDiscount} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-green-500 to-emerald-400 text-dark font-black text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-[0_8px_35px_rgba(74,222,128,0.35)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-327">
                  APLICAR DESCUENTO
                  <ChevronRight className="w-5 h-5" data-id="element-328" />
                </motion.button>

                <button onClick={handleDeclineAll} className="mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-329">
                  No gracias, ir al checkout sin bono
                </button>
              </div>
            </div>
          </motion.div>}
      </div>
    </AnimatePresence>;
}